<template>
	<div class="shared-stepper">
		<div
			v-for="(step, index) in steps"
			:key="index"
			class="shared-stepper__step"
			:class="[
				{ 'shared-stepper__step--clickable': clickable && setup.stepIsPassed(index) },
				{ 'shared-stepper__step--is-active': setup.stepIsActive(step.name) },
				{ 'shared-stepper__step--is-passed': setup.stepIsPassed(index) },
				{ 'shared-stepper__step--is-max': setup.stepIsMax(index) },
				{ 'shared-stepper__step--is-last': index === (steps.length - 1 ) },
				{ 'shared-stepper__step--is-first': index === 0 },
				{ 'shared-stepper__step--with-chevron': withChevron },
			]"
			@click="setup.goToStep(step, index)">
			<div
				class="shared-stepper__step-index"
				:class="`shared-stepper__step-index-${step.color}`">
				<o-icon
					v-if="(setup.stepIsPassed(index) && !setup.stepIsMax(index) && !setup.stepIsActive(step.name))
						|| (withChevron && setup.stepIsPassed(index) && !setup.stepIsActive(step.name))
						|| (withChevron && setup.stepIsMax(index) && index === (steps.length - 1 ))
						|| step.icon"
					:icon="step.icon ?? 'check'"/>
				<span v-else>{{ index + 1 }}</span>
			</div>
			<div
				v-if="!setup.responsive.onPhone || setup.stepIsActive(step.name)"
				class="shared-stepper__step-label"
				:style="`color: ${step.labelColor}`">
				<slot
					:name="`label(${step.name})`"
					v-bind="{ step }">
					{{ step.label }}
				</slot>
			</div>
			<o-icon
				v-if="withChevron && index !== (steps.length - 1)"
				class="shared-stepper__step-next-icon"
				font-icon="icon-interface-arrows-button-right"/>
		</div>
	</div>
</template>

<script setup lang="ts">
// #region Setup
import type { SharedStepperEmits, SharedStepperProps } from './SharedStepperSetup';
import SharedStepperSetup from './SharedStepperSetup';
const emits = defineEmits<SharedStepperEmits>() as SharedStepperEmits;
const props = withDefaults(defineProps<SharedStepperProps>(), SharedStepperSetup.defaultProps);
const setup = new SharedStepperSetup(props, emits);
// #endregion
</script>

<style lang="less" scoped>
@import "@/styles/variables";
@import "@/styles/mixins.less";

.shared-stepper {
	display: flex;
	justify-content: center;

	&__step {
		position: relative;
		display: flex;
		flex: 0 0 8.75rem;
		flex-direction: column;
		gap: 0.5rem;
		align-items: center;

		&-index {
			position: relative;
			z-index: 1;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 2rem;
			height: 2rem;
			border-radius: 50%;
			font-size: 0.85rem;
			font-weight: 600;
			color: var(--grey-darker);
			background: var(--grey-light);
			transition: all 0.3s;

			.shared-stepper__step--with-chevron & {
				width: 1.5rem;
				min-width: 1.5rem;
				height: 1.5rem;
			}

			.shared-stepper__step--with-chevron:not(.shared-stepper__step--is-active) & {
				border: 0.02rem solid var(--grey-light);
				color: var(--grey);
				background: unset;
			}

			.shared-stepper__step--with-chevron.shared-stepper__step--is-passed &,
			.shared-stepper__step--with-chevron.shared-stepper__step--is-passed.shared-stepper__step--is-last & {
				border: unset;
				background: var(--brand);

				.orion-icon {
					color: white;
				}
			}

			/* stylelint-disable-next-line no-descending-specificity */
			.shared-stepper__step--with-chevron.shared-stepper__step--is-active & {
				background: var(--info);
			}

			&-warning {
				border: unset !important;
				color: white !important;
				background: var(--warning) !important;
			}

			&-info {
				border: unset !important;
				color: white !important;
				background: var(--info) !important;
			}
		}

		&-label {
			font-size: 0.9rem;
			font-weight: 600;
			color: var(--grey);
			transition: all 0.3s;
		}

		&--clickable {
			cursor: pointer;
		}

		&--with-chevron {
			flex: unset;
			flex-direction: row;

			&.shared-stepper__step--is-active {
				.shared-stepper__step-label {
					color: black;
				}
			}

			&.shared-stepper__step--is-passed&:not(.shared-stepper__step--is-active),
			&.shared-stepper__step--is-active.shared-stepper__step--is-last {
				/* stylelint-disable-next-line no-descending-specificity */
				.shared-stepper__step-label {
					color: var(--brand);
				}
			}
		}

		&-next-icon {
			margin-right: 1rem;
			margin-left: 1rem;
			font-size: 0.75rem;
			font-weight: 600;
			color: black;
		}

		&:not(.shared-stepper__step--is-last)&:not(.shared-stepper__step--with-chevron)::after {
			content: "";
			position: absolute;
			z-index: 0;
			transform: translate(50%, 0.9rem);
			width: 100%;
			height: 0.125rem;
			background-color: var(--grey-light);
		}

		&--is-passed {
			opacity: 1;
			transition: opacity 0.2s;

			&:not(.shared-stepper__step--is-active) {
				&:hover {
					.shared-stepper__step-index {
						box-shadow: unset;
					}
				}
			}

			&:not(.shared-stepper__step--with-chevron)::after {
				background-color: var(--info) !important;
			}

			/* stylelint-disable-next-line no-descending-specificity */
			.shared-stepper__step-index {
				border: 2px solid var(--info);
				font-size: 1rem;
				color: var(--info);
				background: white;
			}

			/* stylelint-disable-next-line no-descending-specificity */
			.shared-stepper__step-label {
				color: var(--grey-darker);
			}
		}

		&--is-active {
			opacity: 1;

			&::after {
				background-color: var(--info) !important;
			}

			/* stylelint-disable-next-line no-descending-specificity */
			.shared-stepper__step-index {
				border: unset;
				color: white;
				background: var(--info);
			}

			/* stylelint-disable-next-line no-descending-specificity */
			.shared-stepper__step-label {
				color: var(--info);
			}
		}

		&--is-max {
			&:not(.shared-stepper__step--with-chevron)::after {
				background: linear-gradient(to right, var(--info) 50%, var(--grey-light) 50%, var(--grey-light)) !important;
				animation: 1s linear linear-loading infinite;
			}
		}

		&--is-first {
			&:not(.shared-stepper__step--with-chevron)::before {
				content: "";
				position: absolute;
				z-index: 0;
				// background-color: var(--grey);
				transform: translate(-50%, 0.9rem);
				width: 100%;
				height: 0.125rem;
				background: linear-gradient(to right, transparent, var(--info));
			}
		}

		&--is-last {
			&:not(.shared-stepper__step--is-passed)&:not(.shared-stepper__step--with-chevron)::after {
				content: "";
				position: absolute;
				z-index: 0;
				transform: translate(50%, 0.9rem);
				width: 100%;
				height: 0.125rem;
				background: linear-gradient(to right, var(--grey-light), transparent) !important;
			}

			&:not(.shared-stepper__step--with-chevron)::after {
				content: "";
				position: absolute;
				z-index: 0;
				// background-color: var(--grey);
				transform: translate(50%, 0.9rem);
				width: 100%;
				height: 0.125rem;
				background: linear-gradient(to right, var(--info), transparent) !important;
			}
		}
	}
}

@keyframes linear-loading {
	from {
		background: linear-gradient(to right, var(--info) 50%, var(--grey-light) 50%, var(--grey-light));
	}

	to {
		background: linear-gradient(to right, var(--info) 100%, var(--grey-light) 100%, var(--grey-light));
	}
}
</style>
